<template>
  <div class="nav" role="banner" style="position:fixed;top:0;left:0;right:0;z-index:100;background:#fff;border-bottom:1px solid #ede8de;">
    <div class="nav__inner" style="display:flex;flex-direction:row;align-items:center;justify-content:flex-start;flex-wrap:nowrap;height:56px;width:100%;padding:0 32px;box-sizing:border-box;">

      <!-- Logo + brand -->
      <router-link to="/" class="nav__brand" style="display:flex;align-items:center;gap:10px;text-decoration:none;flex-shrink:0;margin-right:32px;">
        <div class="nav__logo">
          <img :src="$app_image + 'Logo_SHC_Transparent.png'" alt="SHC" />
        </div>
        <span class="nav__brand-name">SONG HAN CAPITAL</span>
      </router-link>

      <!-- Desktop links -->
      <div class="nav__links" style="display:flex;flex-direction:row;align-items:center;gap:4px;flex-shrink:0;">
        <router-link
          v-for="(item, index) in menuItems"
          :key="index"
          :to="item.to"
          @click="setActiveMenu(index)"
          :class="['nav__link', { 'nav__link--active': item.active }]"
        >
          {{ item.label }}
        </router-link>

      </div>

      <!-- Right actions -->
      <div class="nav__actions" style="display:flex;flex-direction:row;align-items:center;gap:8px;flex-shrink:0;margin-left:auto;">
        <!-- Language -->
        <div class="nav__lang">
          <LanguageSelect
            @languageChanged="onLanguageChange"
            :responsive="1"
            :current-language="currentLanguage"
          />
        </div>

        <!-- Login -->
        <a
          href="https://app.songhancapital.com/"
          target="_blank"
          rel="noopener noreferrer"
          class="nav__btn nav__btn--outline"
        >
          {{ $t('header.login') }}
        </a>
      </div>

      <!-- Mobile language -->
      <div class="nav__mobile-lang-top">
        <LanguageSelect
          @languageChanged="onLanguageChange"
          :responsive="1"
          :current-language="currentLanguage"
        />
      </div>

      <!-- Mobile hamburger -->
      <button class="nav__hamburger" @click="toggleMobileMenu" aria-label="Menu">
        <span :class="{ open: mobileMenuOpen }"></span>
        <span :class="{ open: mobileMenuOpen }"></span>
        <span :class="{ open: mobileMenuOpen }"></span>
      </button>
    </div>

    <!-- Mobile dropdown -->
    <div :class="['nav__mobile', { 'nav__mobile--open': mobileMenuOpen }]">
      <router-link
        v-for="(item, index) in menuItems"
        :key="index"
        :to="item.to"
        @click="setActiveMenu(index)"
        :class="['nav__mobile-link', { 'nav__mobile-link--active': item.active }]"
      >
        {{ item.label }}
      </router-link>

      <div class="nav__mobile-actions">
        <a
          href="https://app.songhancapital.com/"
          target="_blank"
          rel="noopener noreferrer"
          class="nav__btn nav__btn--outline nav__mobile-btn"
        >
          {{ $t('header.login') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import LanguageSelect from '@/components/language/language-select.vue';
import { currentLocale } from '@/config/i18n.js';
import { ref } from 'vue';

export default {
  name: 'AppNav',
  components: { LanguageSelect },

  setup() {
    const languageDefault = ['vi', 'en', 'fr', 'es'];
    const lang = navigator.language.split('-')[0];
    const language = languageDefault.includes(lang) ? lang : 'en';
    const currentLanguage = ref(localStorage.getItem('language') || language);
    localStorage.setItem('language', currentLanguage.value);

    const onLanguageChange = (lang) => {
      currentLocale.value = lang;
      localStorage.setItem('language', lang);
      currentLanguage.value = lang;
      window.location.reload();
    };

    return { onLanguageChange, currentLanguage };
  },

  data() {
    return {
      mobileMenuOpen: false,
      menuItems: [],
    };
  },

  created() {
    this.buildMenuFromRouter();
    this.handleBrowserBack();
  },

  watch: {
    $route: {
      handler() { this.updateActiveMenu(); },
      immediate: true,
    },
  },

  methods: {
    buildMenuFromRouter() {
      const homePageRoute = this.$router.options.routes.find(
        (route) => route.name === 'HomePage'
      );
      if (homePageRoute?.children) {
        this.menuItems = homePageRoute.children
          .filter(
            (route) =>
              !route.meta?.error &&
              route.name !== 'Home' &&
              !route.meta?.isBlog &&
              !route.meta?.isHidden &&
              !route.meta?.isExternal
          )
          .map((route) => ({
            label: route.meta?.title || route.name,
            to: route.path,
            active: false,
            isExternal: route.path === '/help',
          }));

      }
      this.updateActiveMenu();
    },

    updateActiveMenu() {
      if (!this.menuItems.length) return;
      this.menuItems.forEach((item) => {
        const itemPath = item.to.startsWith('/') ? item.to : '/' + item.to;
        item.active =
          this.$route.path === itemPath ||
          this.$route.matched.some((m) => m.path === itemPath);
      });
    },

    setActiveMenu(index) {
      if (index < 0 || index >= this.menuItems.length) return;
      this.mobileMenuOpen = false;
      const target = this.menuItems[index];
      if (!target) return;
      if (target.to === '/help') {
        this.saveCurrentRoute();
        window.open('https://support.tvsi.com.vn/', '_blank');
        return;
      }
      if (this.$route.path !== target.to) {
        this.$router.push(target.to);
      }
    },

    saveCurrentRoute() {
      sessionStorage.setItem(
        'previousRoute',
        JSON.stringify({
          path: this.$route.path,
          name: this.$route.name,
          params: this.$route.params,
          query: this.$route.query,
          timestamp: Date.now(),
        })
      );
    },

    handleBrowserBack() {
      window.addEventListener('focus', this.checkAndRestoreRoute);
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.checkAndRestoreRoute();
      });
    },

    checkAndRestoreRoute() {
      const saved = sessionStorage.getItem('previousRoute');
      if (!saved) return;
      try {
        const data = JSON.parse(saved);
        if (Date.now() - data.timestamp < 300000 && this.$route.path !== data.path) {
          sessionStorage.removeItem('previousRoute');
          this.$router.push({ path: data.path, params: data.params, query: data.query });
        }
      } catch {
        sessionStorage.removeItem('previousRoute');
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },

    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.checkAndRestoreRoute);
  },
};
</script>

<style src="./AppNav.scss" lang="scss"></style>
